import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useOrganizations } from '@/hooks/useOrganizations';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2, ChevronsUpDown, Settings } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';

interface OrganizationSelectorProps {
  className?: string;
}

export function OrganizationSelector({ className }: OrganizationSelectorProps) {
  const {
    organizations,
    selectedOrganization,
    setSelectedOrganizationId,
    isLoading,
  } = useOrganizations();
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const handleOrganizationChange = (newOrgId: string) => {
    if (newOrgId === selectedOrganization?.id) return;

    // Update the selected organization
    setSelectedOrganizationId(newOrgId);

    // Invalidate all queries to refresh data for the new organization
    queryClient.invalidateQueries();

    // Show a toast notification
    const newOrg = organizations.find((org) => org.id === newOrgId);
    if (newOrg) {
      toast.success(`Switched to ${newOrg.name}`);
    }
  };

  if (isLoading) {
    return (
      <div className={cn('w-full', className)}>
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (!organizations || organizations.length === 0) {
    return (
      <div
        className={cn(
          'w-full h-10 px-3 bg-background/50 border border-white/10 rounded-md flex items-center',
          className
        )}
      >
        <span className="text-sm text-muted-foreground">No organizations</span>
      </div>
    );
  }

  return (
    <Select
      value={selectedOrganization?.id}
      onValueChange={handleOrganizationChange}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <SelectTrigger
        className={cn(
          'w-full h-10 bg-background/60 border border-border/60 hover:bg-muted/60 dark:border-white/10 dark:hover:bg-white/5 transition-all',
          'focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50',
          'cursor-pointer',
          isOpen && 'bg-muted/70 dark:bg-white/5 border-purple-400/40 dark:border-white/20 shadow-[0_8px_20px_rgba(15,23,42,0.4)]',
          className
        )}
      >
        <div className="flex items-center gap-2 w-full">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-linear-to-br from-purple-500/20 to-blue-500/10 ring-1 ring-purple-400/30 dark:ring-white/10">
            <Building2 className="h-3.5 w-3.5 text-purple-600 dark:text-white/80" />
          </div>
          <div className="flex-1 text-left min-w-0">
            <SelectValue>
              {selectedOrganization ? (
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium truncate max-w-[140px]" title={selectedOrganization.name}>
                    {selectedOrganization.name}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    Organization
                  </span>
                </div>
              ) : (
                <span className="text-sm text-muted-foreground">Select organization</span>
              )}
            </SelectValue>
          </div>
          <ChevronsUpDown
            className={cn(
              'h-4 w-4 opacity-50 transition-transform duration-200',
              isOpen && '-rotate-180'
            )}
          />
        </div>
      </SelectTrigger>
      <SelectContent
        className="bg-background/95 backdrop-blur-md border border-border/60 dark:border-white/10 rounded-xl shadow-[0_20px_45px_rgba(15,23,42,0.55)] p-1"
        align="start"
        sideOffset={4}
      >
        <div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Organizations
        </div>
        {organizations.map((org) => (
          <SelectItem
            key={org.id}
            value={org.id}
            className="py-2 px-2 cursor-pointer rounded-lg hover:bg-muted/50 dark:hover:bg-white/5 data-[state=checked]:bg-purple-500/20 data-[state=checked]:text-purple-950 dark:data-[state=checked]:text-white"
          >
            <div className="flex items-center gap-3 pr-6">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-linear-to-br from-purple-500/20 to-blue-500/10 ring-1 ring-purple-400/30 dark:ring-white/10 flex-shrink-0">
                <Building2 className="h-3.5 w-3.5 text-purple-600 dark:text-white/80" />
              </div>
              <span className="text-sm truncate" title={org.name}>
                {org.name}
              </span>
            </div>
          </SelectItem>
        ))}

        <Separator className="my-1 bg-white/10" />

        {/* Organization Settings Link */}
        <div className="px-1 py-1">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2 h-9 px-2 text-sm hover:bg-muted/60 dark:hover:bg-white/5 cursor-pointer"
            onClick={() => {
              setIsOpen(false);
              navigate({ to: '/organization/settings' });
            }}
          >
            <Settings className="h-4 w-4 text-muted-foreground" />
            Organization Settings
          </Button>
        </div>
      </SelectContent>
    </Select>
  );
}
